import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@shared/lib/supabase';
import { RefreshCw, Copy, Eye, EyeOff } from 'lucide-react';

interface Merchant {
  id: string;
  email: string;
  business_name: string | null;
  api_key: string;
  webhook_secret: string;
  default_wallet: string;
  is_active: boolean | null;
  stripe_account_id: string | null;
  stripe_onboarded: boolean;
  created_at: string;
}

export default function AdminMerchants() {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadMerchants();
  }, []);

  const loadMerchants = async () => {
    const { data } = await supabase
      .from('merchants')
      .select('id, email, business_name, api_key, webhook_secret, default_wallet, is_active, stripe_account_id, stripe_onboarded, created_at')
      .order('created_at', { ascending: false });
    setMerchants((data as Merchant[]) || []);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    if (!search) return merchants;
    const q = search.toLowerCase();
    return merchants.filter(
      (m) =>
        m.business_name?.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.id.toLowerCase().includes(q) ||
        m.default_wallet.toLowerCase().includes(q),
    );
  }, [merchants, search]);

  const activeCount = merchants.filter((m) => m.is_active).length;
  const stripeCount = merchants.filter((m) => m.stripe_onboarded).length;

  const rotateApiKey = async (merchantId: string) => {
    if (!confirm('Rotate this merchant\'s API key? Their current key will stop working immediately.')) return;
    setRotatingId(merchantId);
    const { data, error } = await supabase.rpc('rotate_merchant_api_key', {
      merchant_uuid: merchantId,
    });
    if (!error && data) {
      setMerchants((prev) =>
        prev.map((m) => (m.id === merchantId ? { ...m, api_key: data } : m)),
      );
      setVisibleKeys((prev) => new Set([...prev, merchantId]));
    }
    setRotatingId(null);
  };

  const toggleKeyVisibility = (id: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyKey = async (key: string, id: string) => {
    await navigator.clipboard.writeText(key);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const maskKey = (key: string) => `${key.slice(0, 8)}${'*'.repeat(24)}${key.slice(-8)}`;

  function timeAgo(date: string): string {
    const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <p className="font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>Merchants</h1>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>{merchants.length} total</span>
          <span className="font-mono text-xs text-emerald-400">{activeCount} active</span>
          {stripeCount > 0 && (
            <span className="font-mono text-xs text-sky-400">{stripeCount} Stripe</span>
          )}
        </div>
      </div>

      <div className="relative mb-5">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>/</span>
        <input
          type="text"
          placeholder="Search by name, email, ID, or wallet..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-7 pr-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors"
          style={{
            border: '1px solid var(--dash-border)',
            background: 'var(--dash-input)',
            color: 'var(--dash-text-secondary)',
          }}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded p-12 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>
            {search ? 'No merchants match your search.' : 'No merchants yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <div
              key={m.id}
              className="rounded overflow-hidden"
              style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
            >
              <div
                className="px-5 py-4 flex items-center justify-between"
                style={{ borderBottom: '1px solid var(--dash-border-light)' }}
              >
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-sans text-sm font-medium" style={{ color: 'var(--dash-text-primary)' }}>
                      {m.business_name || 'Unnamed Merchant'}
                    </h3>
                    <span
                      className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                        m.is_active
                          ? 'text-green-400 bg-green-400/10 border-green-400/20'
                          : 'text-red-400 bg-red-400/10 border-red-400/20'
                      }`}
                    >
                      {m.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {m.stripe_onboarded ? (
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/20">
                        Stripe
                      </span>
                    ) : m.stripe_account_id ? (
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border text-amber-400 bg-amber-400/10 border-amber-400/20">
                        Stripe Pending
                      </span>
                    ) : null}
                  </div>
                  <p className="font-mono text-[10px] mt-0.5" style={{ color: 'var(--dash-text-dim)' }}>{m.email}</p>
                </div>
                <p className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>
                  Joined {timeAgo(m.created_at)}
                </p>
              </div>

              <div className="px-5 py-4 space-y-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--dash-text-dim)' }}>
                    API Key
                  </p>
                  <div className="flex items-center gap-2">
                    <code
                      className="flex-1 font-mono text-xs rounded px-3 py-2 truncate"
                      style={{
                        color: 'var(--dash-text-tertiary)',
                        background: 'var(--dash-bg)',
                        border: '1px solid var(--dash-border)',
                      }}
                    >
                      {visibleKeys.has(m.id) ? m.api_key : maskKey(m.api_key)}
                    </code>
                    <button
                      onClick={() => toggleKeyVisibility(m.id)}
                      className="w-8 h-8 rounded flex items-center justify-center transition-colors"
                      title={visibleKeys.has(m.id) ? 'Hide' : 'Show'}
                      style={{
                        background: 'var(--dash-hover-bg)',
                        border: '1px solid var(--dash-border)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    >
                      {visibleKeys.has(m.id) ? (
                        <EyeOff size={14} style={{ color: 'var(--dash-text-muted)' }} />
                      ) : (
                        <Eye size={14} style={{ color: 'var(--dash-text-muted)' }} />
                      )}
                    </button>
                    <button
                      onClick={() => copyKey(m.api_key, m.id)}
                      className="w-8 h-8 rounded flex items-center justify-center transition-colors"
                      title="Copy"
                      style={{
                        background: 'var(--dash-hover-bg)',
                        border: '1px solid var(--dash-border)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    >
                      <Copy
                        size={14}
                        className={copiedId === m.id ? 'text-green-400' : ''}
                        style={copiedId === m.id ? undefined : { color: 'var(--dash-text-muted)' }}
                      />
                    </button>
                    <button
                      onClick={() => rotateApiKey(m.id)}
                      disabled={rotatingId === m.id}
                      className="h-8 px-3 rounded flex items-center gap-1.5 transition-colors disabled:opacity-50 font-mono text-[10px] uppercase tracking-widest"
                      title="Rotate API Key"
                      style={{
                        background: 'var(--dash-hover-bg)',
                        border: '1px solid var(--dash-border)',
                        color: 'var(--dash-text-muted)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    >
                      <RefreshCw
                        size={12}
                        className={rotatingId === m.id ? 'animate-spin' : ''}
                      />
                      Rotate
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>
                      Wallet
                    </p>
                    <p className="font-mono text-xs truncate" style={{ color: 'var(--dash-text-muted)' }}>
                      {m.default_wallet}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>
                      ID
                    </p>
                    <p className="font-mono text-xs truncate" style={{ color: 'var(--dash-text-muted)' }}>{m.id}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
